import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import { CheckboxGroupInput, Loading, RadioButtonGroupInput, RecordContextProvider, SimpleForm, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useEffect, useState } from 'react';
import { notificationChannelChoices, reportFrequencyChoices } from '@/components/global/options/selectOptions.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import SaveIcon from '@/assets/icons/save-icon.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';

const ANALYTICAL_REPORT_TYPE = 'ANALYTICAL_REPORT';

const StyledNotificationSettings = styled(Box)(() => ({
    width: '560px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        width: '100%',
    },
    '& .popup-wrapper.popup-wrapper-top': {
        '& .popup-box--header': {
            padding: 0,
        },
    },
    '& .popup-wrapper.popup-wrapper-bottom': {
        padding: '38px 56px 40px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            padding: '40px 20px',
        },
    },
    '& .MuiCardContent-root.notification-settings-form': {
        marginBottom: '44px !important',
    },
    '& .MuiToolbar-root.modal-toolbar': {
        position: 'relative',
        '&:before': {
            content: '""',
            position: 'absolute',
            top: '-33px',
            left: '-56px',
            width: 'calc(100% + 112px)',
            height: '2px',
            backgroundColor: Palette.gray.gray100,
        },
    },
    '& .notification-settings-form > .MuiStack-root': {
        gap: '17px',
    },
}));
export const NotificationSettings = ({ handleClose = () => {} }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        dataProvider
            .get('notification-preference')
            .then(data => setData(data.data))
            .finally(() => setLoading(false));
    }, [dataProvider]);

    const dataByCategories = {};
    let analyticalReportItem = null;
    for (const item of data) {
        if (item.type === ANALYTICAL_REPORT_TYPE) {
            analyticalReportItem = item;
            continue;
        }
        if (!(item.category in dataByCategories)) {
            dataByCategories[item.category] = [];
        }
        dataByCategories[item.category].push(item);
    }

    const categories = Object.keys(dataByCategories);
    const record = Object.fromEntries(data.map(item => [item.type, item.preferredChannels]));
    if (analyticalReportItem) {
        record[analyticalReportItem.type] = analyticalReportItem.preferredFrequency;
    }

    const onSubmitSuccess = () => {
        handleClose();
        notify(translate('notification.settings.settingsSaved'));
    };

    const handleSubmit = values => {
        const items = Object.entries(values).map(([key, value]) => ({
            type: key,
            preferredChannels: key === ANALYTICAL_REPORT_TYPE ? ['email_summary'] : value,
            preferredFrequency: key === ANALYTICAL_REPORT_TYPE ? value : null,
        }));
        return dataProvider
            .put('notification-preference', { data: { items } })
            .then(onSubmitSuccess)
            .catch(error => notify(error.message, { type: 'error' }));
    };

    return (
        <StyledNotificationSettings>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('notification.settings.title')}</Typography>
                    <Box>
                        <CustomCloseButton className='close-button' onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <Box className='popup-wrapper popup-wrapper-bottom'>
                {(loading && <Loading />) || (
                    <RecordContextProvider key='notification-preferences' value={record}>
                        {(data.length === 0 && <Typography>{translate('notification.settings.noAvailableNotifications')}</Typography>) || (
                            <SimpleForm
                                className='notification-settings-form'
                                toolbar={
                                    <TagCategoryToolbar
                                        onCancel={handleClose}
                                        cancelLabel={translate('common.cancel')}
                                        saveLabel={translate('common.save')}
                                        endIcon={<SaveIcon />}
                                    />
                                }
                                onSubmit={handleSubmit}
                            >
                                {categories.map(category => (
                                    <NotificationCategorySection key={category} category={category} data={dataByCategories[category]} />
                                ))}
                                <AnalyticalReportSection item={analyticalReportItem} />
                            </SimpleForm>
                        )}
                    </RecordContextProvider>
                )}
            </Box>
        </StyledNotificationSettings>
    );
};

const StyledCheckboxGroupInput = styled(CheckboxGroupInput)(() => ({
    '& .MuiFormLabel-root.RaCheckboxGroupInput-label': {
        padding: '0',
        marginBottom: '7px',
        transform: 'translate(0, 0) scale(1)',
        '& span': {
            fontSize: '14px',
            color: Palette.black,
            whiteSpace: 'wrap',
        },
    },
    '& .MuiFormControlLabel-root': {
        '& .MuiTypography-root.MuiTypography-body1': {
            fontSize: '14px',
        },
    },
    '& .MuiFormGroup-root.MuiFormGroup-row': {
        marginLeft: '2px',
    },
}));

const StyledNotificationCategorySection = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
    width: '100%',
    '& .MuiTypography-root.MuiTypography-h6': {
        borderBottom: `2px solid ${Palette.gray.gray100}`,
        paddingBottom: '8px',
    },
    '& .MuiFormHelperText-root': {
        display: 'none',
    },
    '& .notification-category-section-item': {
        borderBottom: `2px solid ${Palette.gray.gray100}`,
        padding: '0 20px 25px',
        marginTop: '10px',
    },
}));

const NotificationCategorySection = ({ category, data }) => {
    const translate = useTranslate();
    return (
        <StyledNotificationCategorySection className='notification-category-section'>
            <Typography variant='h6' gutterBottom>
                {translate(`notification.categories.${category}`)}
            </Typography>
            {data.map(item => (
                <StyledCheckboxGroupInput
                    className='notification-category-section-item'
                    key={item.type}
                    source={item.type}
                    label={item.description}
                    choices={notificationChannelChoices(translate, item.preferredFrequency).filter(choice => item.allowedChannels.includes(choice.id))}
                />
            ))}
        </StyledNotificationCategorySection>
    );
};

const AnalyticalReportSection = ({ item }) => {
    const translate = useTranslate();

    if (!item) {
        return null;
    }

    return (
        <StyledNotificationCategorySection className='notification-category-section'>
            <Typography variant='h6' gutterBottom>
                {translate('notification.report.title')}
            </Typography>
            <RadioButtonGroupInput
                className='notification-category-section-item'
                row={false}
                key={item.type}
                source={item.type}
                label={translate('notification.report.label')}
                choices={reportFrequencyChoices(translate)}
            />
        </StyledNotificationCategorySection>
    );
};

NotificationSettings.propTypes = {
    handleClose: PropTypes.func,
};

NotificationCategorySection.propTypes = {
    category: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            preferredChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
            allowedChannels: PropTypes.arrayOf(PropTypes.string).isRequired,
            preferredFrequency: PropTypes.string,
        }),
    ).isRequired,
};
